import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { FaVoteYea } from "react-icons/fa";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import GovernanceAbi from "../abi/CrownGovernanceDao.json";

// Register required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const GOVERNANCE_CONTRACT_ADDRESS = "0xEa00E8B4b58783fC42De6C82fABc93b656e200E7";
const CRW_TOKEN_ADDRESS = "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03";
const OWNER_ADDRESS = "0x00eB49b3940e50966B5dCCC91f7AAdA76483a721"; // Your owner address

const CrownGovernanceDao = ({ provider, account, onTransaction }) => {
  const [contract, setContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [activeTab, setActiveTab] = useState("create");
  const [isLoading, setIsLoading] = useState(false);
  // Create Proposal States
  const [proposalType, setProposalType] = useState("0");
  const [description, setDescription] = useState("");
  const [newValue, setNewValue] = useState("");
  const [targetAddress, setTargetAddress] = useState("");
  const [duration, setDuration] = useState("7");
  const [priceFeed, setPriceFeed] = useState("");
  const [heartbeat, setHeartbeat] = useState("");
  const [decimals, setDecimals] = useState("");
  const [useOracle, setUseOracle] = useState("false");
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");
  // Vote Proposal States
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [voteWeight, setVoteWeight] = useState("");
  const [inFavor, setInFavor] = useState(true);
  const [voteError, setVoteError] = useState("");
  const [voteSuccess, setVoteSuccess] = useState("");
  // Proposal List States
  const [listError, setListError] = useState("");
  // Voting Results States
  const [timeRemaining, setTimeRemaining] = useState({});
  // Proposal states for owner review
  const [proposalStatuses, setProposalStatuses] = useState(() => {
    const saved = localStorage.getItem("proposalStatuses");
    return saved ? JSON.parse(saved) : {};
  });

  const proposalTypes = [
    "Transaction Fee",
    "Daily Reward",
    "Daily Interest",
    "Use Oracle Price",
    "Swap Fee",
    "Maximum Position Count",
    "DEX Pool Fee",
    "Stakeholders Fee Share",
    "Maximum Range Deviation",
    "Blacklist Address",
    "Remove from Blacklist",
    "Add Supported Token",
    "Remove Supported Token",
    "CRW Price Source"
  ];

  useEffect(() => {
    const initContracts = async () => {
      try {
        const signer = await provider.getSigner();
        const governanceContract = new ethers.Contract(
          GOVERNANCE_CONTRACT_ADDRESS,
          GovernanceAbi,
          signer
        );
        const tokenContract = new ethers.Contract(
          CRW_TOKEN_ADDRESS,
          [
            "function approve(address spender, uint256 amount) public returns (bool)",
            "function allowance(address owner, address spender) public view returns (uint256)"
          ],
          signer
        );
        setContract(governanceContract);
        setTokenContract(tokenContract);
      } catch (error) {
        console.error("Error initializing contracts:", error.message);
      }
    };
    if (provider && account) {
      initContracts();
    }
  }, [provider, account]);

  useEffect(() => {
    const updateRequiredFields = () => {
      const type = parseInt(proposalType);
      let fields = { description: true, duration: true };
      switch (type) {
        case 0: // Transaction Fee
        case 1: // Daily Reward
        case 2: // Daily Interest
          fields.newValue = true;
          fields.unit = "%";
          break;
        case 3: // Use Oracle Price
          fields.useOracle = true;
          fields.unit = "boolean";
          break;
        case 4: // Swap Fee
        case 6: // DEX Pool Fee
          fields.newValue = true;
          fields.unit = "%";
          break;
        case 5: // Maximum Position Count
          fields.newValue = true;
          fields.unit = "count";
          break;
        case 7: // Stakeholders Fee Share
        case 8: // Maximum Range Deviation
          fields.newValue = true;
          fields.unit = "%";
          break;
        case 9: // Blacklist Address
        case 10: // Remove from Blacklist
        case 12: // Remove Supported Token
          fields.targetAddress = true;
          fields.unit = "address";
          break;
        case 11: // Add Supported Token
          fields.targetAddress = true;
          fields.priceFeed = true;
          fields.heartbeat = true;
          fields.decimals = true;
          fields.unit = "address, hours, decimal places";
          break;
        case 13: // CRW Price Source
          fields.targetAddress = true;
          fields.heartbeat = true;
          fields.unit = "address, hours";
          break;
        default:
          fields.newValue = false;
      }
      setRequiredFields(fields);
    };
    updateRequiredFields();
  }, [proposalType, proposalTypes.length]);

  useEffect(() => {
    const fetchProposals = async () => {
      if (!contract) return;
      setIsLoading(true);
      try {
        const count = await contract.proposalCount();
        const allProposals = [];
        for (let i = 1; i <= count; i++) {
          const proposal = await contract.getProposal(i);
          allProposals.push({
            id: Number(proposal.id),
            proposer: proposal.proposer,
            description: proposal.description,
            newValue: proposal.newValue,
            targetAddress: proposal.targetAddress,
            startTime: Number(proposal.startTime),
            endTime: Number(proposal.endTime),
            totalVotesFor: ethers.formatEther(proposal.totalVotesFor),
            totalVotesAgainst: ethers.formatEther(proposal.totalVotesAgainst),
            totalCollectedTokens: ethers.formatEther(proposal.totalCollectedTokens),
            executed: proposal.executed,
            vetoed: proposal.vetoed,
            ownerReviewed: proposal.ownerReviewed,
            proposalType: Number(proposal.proposalType),
            functionId: Number(proposal.functionId)
          });
        }
        setProposals(allProposals);
      } catch (err) {
        setListError("Error loading proposals: " + err.message);
        setVoteError("Error loading proposals: " + err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProposals();

    const interval = setInterval(fetchProposals, 60000); // Changed to 1 minute (60000 ms)
    return () => clearInterval(interval);
  }, [contract]);

  useEffect(() => {
    const updateCountdown = () => {
      const now = Math.floor(Date.now() / 1000);
      const updatedTimeRemaining = {};
      proposals.forEach((proposal) => {
        if (!proposal.executed && !proposal.vetoed && proposal.endTime > now) {
          const secondsLeft = proposal.endTime - now;
          const days = Math.floor(secondsLeft / (24 * 3600));
          const hours = Math.floor((secondsLeft % (24 * 3600)) / 3600);
          const minutes = Math.floor((secondsLeft % 3600) / 60);
          const seconds = secondsLeft % 60;
          updatedTimeRemaining[proposal.id] = {
            days,
            hours,
            minutes,
            seconds
          };
        } else {
          updatedTimeRemaining[proposal.id] = null;
        }
      });
      setTimeRemaining(updatedTimeRemaining);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000); // Remains 1 second
    return () => clearInterval(interval);
  }, [proposals]);

  useEffect(() => {
    localStorage.setItem("proposalStatuses", JSON.stringify(proposalStatuses));
  }, [proposalStatuses]);

  const checkAndApproveCRW = async (amountInCRW) => {
    if (!tokenContract || !account) {
      throw new Error("Token contract or account is not initialized");
    }
    try {
      const amount = ethers.parseEther(amountInCRW.toString());
      const allowance = await tokenContract.allowance(account, GOVERNANCE_CONTRACT_ADDRESS);
      if (ethers.toBigInt(allowance) < ethers.toBigInt(amount)) {
        console.log("Approving CRW:", amountInCRW, "CRW");
        const tx = await tokenContract.approve(GOVERNANCE_CONTRACT_ADDRESS, amount);
        await tx.wait();
        console.log("CRW successfully approved");
        return true;
      }
      console.log("Sufficient allowance already exists");
      return false;
    } catch (err) {
      throw new Error("Error approving CRW: " + err.message);
    }
  };

  const validateNewValue = (type, value) => {
    const numValue = parseFloat(value);
    switch (type) {
      case 0: // Transaction Fee
      case 1: // Daily Reward
      case 2: // Daily Interest
        if (isNaN(numValue) || numValue <= 0 || numValue > 1) {
          throw new Error("Value must be between 0.01% and 1%");
        }
        break;
      case 4: // Swap Fee
        if (isNaN(numValue) || numValue < 0.1 || numValue > 3) {
          throw new Error("Value must be between 0.1% and 3%");
        }
        break;
      case 5: // Maximum Position Count
        if (isNaN(numValue) || numValue <= 0 || !Number.isInteger(numValue)) {
          throw new Error("Value must be an integer greater than 0");
        }
        break;
      case 6: // DEX Pool Fee
        if (isNaN(numValue) || numValue < 0.1 || numValue > 10) {
          throw new Error("Value must be between 0.1% and 10%");
        }
        break;
      case 7: // Stakeholders Fee Share
      case 8: // Maximum Range Deviation
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          throw new Error("Value must be between 0% and 100%");
        }
        break;
      case 9: // Blacklist Address
      case 10: // Remove from Blacklist
      case 12: // Remove Supported Token
      case 11: // Add Supported Token
      case 13: // CRW Price Source
        if (!ethers.isAddress(value)) {
          throw new Error("Invalid address");
        }
        break;
      default:
        break;
    }
  };

  const handleCreateProposal = async () => {
    setCreateError("");
    setCreateSuccess("");
    setIsLoading(true);
    try {
      if (!contract || !account) {
        throw new Error("Contract or account is not initialized");
      }
      if (!description) {
        throw new Error("Description is required");
      }
      if (!duration || duration < 7 || duration > 28) {
        throw new Error("Duration must be between 7 and 28 days");
      }
      const type = parseInt(proposalType);
      let value = "0";
      const addr = targetAddress || "0x0000000000000000000000000000000000000000";
      const dur = parseInt(duration);

      if (requiredFields.newValue) {
        validateNewValue(type, newValue);
        switch (type) {
          case 0:
          case 1:
          case 2:
            value = Math.round(parseFloat(newValue) * 100).toString();
            break;
          case 4:
          case 6:
            value = Math.round(parseFloat(newValue) * 1000).toString();
            break;
          case 5:
            value = Math.round(parseFloat(newValue)).toString();
            break;
          case 7:
          case 8:
            value = Math.round(parseFloat(newValue) * 100).toString();
            break;
          default:
            break;
        }
      } else if (requiredFields.useOracle) {
        value = useOracle === "true" ? "1" : "0";
      } else if (type === 11) {
        if (!ethers.isAddress(targetAddress)) {
          throw new Error("Invalid token address");
        }
        if (!ethers.isAddress(priceFeed)) {
          throw new Error("Invalid price feed address");
        }
        if (!decimals || parseInt(decimals) <= 0 || parseInt(decimals) > 18) {
          throw new Error("Decimal places must be between 1 and 18");
        }
        if (!heartbeat || parseFloat(heartbeat) <= 0) {
          throw new Error("Heartbeat must be greater than 0 hours");
        }
        const heartbeatSeconds = Math.round(parseFloat(heartbeat) * 3600);
        value = ethers.AbiCoder.defaultAbiCoder().encode(
          ["address", "uint256", "uint8"],
          [priceFeed, heartbeatSeconds, parseInt(decimals)]
        );
      } else if (type === 13) {
        if (!ethers.isAddress(targetAddress)) {
          throw new Error("Invalid price feed address");
        }
        if (!heartbeat || parseFloat(heartbeat) <= 0) {
          throw new Error("Heartbeat must be greater than 0 hours");
        }
        const heartbeatSeconds = Math.round(parseFloat(heartbeat) * 3600);
        value = ethers.AbiCoder.defaultAbiCoder().encode(
          ["address", "uint256"],
          [targetAddress, heartbeatSeconds]
        );
      }

      await checkAndApproveCRW(100);

      console.log("Creating proposal with parameters:", {
        type,
        description,
        value: value.toString(),
        addr,
        dur
      });
      const tx = await contract.createProposal(type, description, value, addr, dur, 0);
      const receipt = await tx.wait();
      onTransaction(receipt);
      setCreateSuccess("Proposal was successfully created!");
      const count = await contract.proposalCount();
      const allProposals = [];
      for (let i = 1; i <= count; i++) {
        const proposal = await contract.getProposal(i);
        allProposals.push({
          id: Number(proposal.id),
          proposer: proposal.proposer,
          description: proposal.description,
          newValue: proposal.newValue,
          targetAddress: proposal.targetAddress,
          startTime: Number(proposal.startTime),
          endTime: Number(proposal.endTime),
          totalVotesFor: ethers.formatEther(proposal.totalVotesFor),
          totalVotesAgainst: ethers.formatEther(proposal.totalVotesAgainst),
          totalCollectedTokens: ethers.formatEther(proposal.totalCollectedTokens),
          executed: proposal.executed,
          vetoed: proposal.vetoed,
          ownerReviewed: proposal.ownerReviewed,
          proposalType: Number(proposal.proposalType),
          functionId: Number(proposal.functionId)
        });
      }
      setProposals(allProposals);
    } catch (err) {
      setCreateError("Error creating proposal: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVote = async () => {
    setVoteError("");
    setVoteSuccess("");
    setIsLoading(true);
    try {
      if (!contract || !account || !selectedProposal) {
        throw new Error("Contract, account, or proposal is not initialized");
      }
      if (!voteWeight || isNaN(voteWeight) || parseFloat(voteWeight) <= 0) {
        throw new Error("A valid vote weight is required");
      }
      await checkAndApproveCRW(voteWeight);
      const weight = ethers.parseEther(voteWeight);
      const tx = await contract.vote(selectedProposal.id, inFavor, weight);
      const receipt = await tx.wait();
      onTransaction(receipt);
      setVoteSuccess("Vote was successfully submitted!");
    } catch (err) {
      setVoteError("Error voting: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getValueDisplay = (type, value) => {
    switch (type) {
      case 0:
      case 1:
      case 2:
        return `${(Number(value) / 100).toFixed(2)} %`;
      case 3:
        return value === "1" ? "Yes" : "No";
      case 4:
      case 6:
        return `${(Number(value) / 1000).toFixed(2)} %`;
      case 5:
        return `${Number(value)}`;
      case 7:
      case 8:
        return `${(Number(value) / 100).toFixed(2)} %`;
      case 9:
      case 10:
      case 12:
        return value;
      case 11:
        try {
          const decoded = ethers.AbiCoder.defaultAbiCoder().decode(
            ["address", "uint256", "uint8"],
            value
          );
          return `Feed: ${decoded[0]}, Heartbeat: ${(Number(decoded[1]) / 3600).toFixed(2)} hours, Decimals: ${decoded[2]}`;
        } catch {
          return value;
        }
      case 13:
        try {
          const decoded = ethers.AbiCoder.defaultAbiCoder().decode(
            ["address", "uint256"],
            value
          );
          return `Feed: ${decoded[0]}, Heartbeat: ${(Number(decoded[1]) / 3600).toFixed(2)} hours`;
        } catch {
          return value;
        }
      default:
        return value;
    }
  };

  const getPieChartData = (proposal) => {
    const votesFor = parseFloat(proposal.totalVotesFor);
    const votesAgainst = parseFloat(proposal.totalVotesAgainst);
    const total = votesFor + votesAgainst;

    if (total === 0) {
      return {
        labels: ["No votes"],
        datasets: [
          {
            data: [1],
            backgroundColor: ["#e0e0e0"],
            borderColor: ["#ffffff"],
            borderWidth: 1
          }
        ]
      };
    }

    return {
      labels: ["For", "Against"],
      datasets: [
        {
          data: [votesFor, votesAgainst],
          backgroundColor: ["#4caf50", "#f44336"],
          borderColor: ["#ffffff"],
          borderWidth: 1
        }
      ]
    };
  };

  const [requiredFields, setRequiredFields] = useState({});

  const getStatus = (proposal) => {
    const now = Math.floor(Date.now() / 1000);
    const gracePeriod = 3 * 24 * 60 * 60; // 3 days in seconds
    if (proposalStatuses[proposal.id] === "approved") {
      return "Approved";
    } else if (proposalStatuses[proposal.id] === "rejected") {
      return "Rejected";
    } else if (proposal.executed) {
      return "Executed";
    } else if (proposal.vetoed) {
      return "Vetoed";
    } else if (proposal.endTime + gracePeriod < now && !proposal.ownerReviewed) {
      return "Proposal is under review by the blockchain council";
    } else if (proposal.endTime < now) {
      return "Proposal is under review by the blockchain council";
    } else {
      return "Active";
    }
  };

  const handleReview = (proposalId, status) => {
    if (account?.toLowerCase() !== OWNER_ADDRESS.toLowerCase()) {
      alert("This action can only be performed by the owner!");
      return;
    }
    setProposalStatuses((prev) => ({
      ...prev,
      [proposalId]: status
    }));
  };

  return (
    <div className="crown-governance-dao">
      <h2>
        <FaVoteYea className="icon" /> Governance DAO
      </h2>
      {isLoading && <p>Loading...</p>}
      <div className="tabs">
        <button
          className={`tab ${activeTab === "create" ? "active" : ""}`}
          onClick={() => setActiveTab("create")}
        >
          Create Proposal
        </button>
        <button
          className={`tab ${activeTab === "vote" ? "active" : ""}`}
          onClick={() => setActiveTab("vote")}
        >
          Vote on Proposals
        </button>
        <button
          className={`tab ${activeTab === "view" ? "active" : ""}`}
          onClick={() => setActiveTab("view")}
        >
          View Proposals
        </button>
        <button
          className={`tab ${activeTab === "results" ? "active" : ""}`}
          onClick={() => setActiveTab("results")}
        >
          Voting Results
        </button>
      </div>
      <div className="tab-content">
        {activeTab === "create" && (
          <div className="create-proposal action-box">
            <h3>Create New Proposal</h3>
            <div className="form-group">
              <label>Proposal Type</label>
              <select
                value={proposalType}
                onChange={(e) => setProposalType(e.target.value)}
                className="input"
                disabled={isLoading}
              >
                {proposalTypes.map((type, index) => (
                  <option key={index} value={index}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            {requiredFields.description && (
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the proposal"
                  className="input"
                  disabled={isLoading}
                />
              </div>
            )}
            {requiredFields.newValue && (
              <div className="form-group">
                <label>New Value ({requiredFields.unit})</label>
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder={`Enter value (e.g. ${requiredFields.unit === "%" ? "0.1" : "100"})`}
                  className="input"
                  disabled={isLoading}
                />
              </div>
            )}
            {requiredFields.useOracle && (
              <div className="form-group">
                <label>Use Oracle Price</label>
                <select
                  value={useOracle}
                  onChange={(e) => setUseOracle(e.target.value)}
                  className="input"
                  disabled={isLoading}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            )}
            {requiredFields.targetAddress && (
              <div className="form-group">
                <label>{proposalType === "11" ? "Token Address" : proposalType === "13" ? "Price Feed Address" : "Target Address"}</label>
                <input
                  type="text"
                  value={targetAddress}
                  onChange={(e) => setTargetAddress(e.target.value)}
                  placeholder="Enter address"
                  className="input"
                  disabled={isLoading}
                />
              </div>
            )}
            {requiredFields.priceFeed && (
              <div className="form-group">
                <label>Price Feed Address</label>
                <input
                  type="text"
                  value={priceFeed}
                  onChange={(e) => setPriceFeed(e.target.value)}
                  placeholder="Enter price feed address"
                  className="input"
                  disabled={isLoading}
                />
              </div>
            )}
            {requiredFields.heartbeat && (
              <div className="form-group">
                <label>Heartbeat (hours)</label>
                <input
                  type="text"
                  value={heartbeat}
                  onChange={(e) => setHeartbeat(e.target.value)}
                  placeholder="Enter heartbeat in hours (e.g. 1)"
                  className="input"
                  disabled={isLoading}
                />
              </div>
            )}
            {requiredFields.decimals && (
              <div className="form-group">
                <label>Number of Decimal Places</label>
                <input
                  type="number"
                  value={decimals}
                  onChange={(e) => setDecimals(e.target.value)}
                  placeholder="Enter number of decimal places (e.g. 18)"
                  className="input"
                  disabled={isLoading}
                />
              </div>
            )}
            {requiredFields.duration && (
              <div className="form-group">
                <label>Duration (days)</label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min="7"
                  max="28"
                  className="input"
                  disabled={isLoading}
                />
              </div>
            )}
            <button
              onClick={handleCreateProposal}
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Create Proposal"}
            </button>
            {createError && <p className="error">{createError}</p>}
            {createSuccess && <p className="success">{createSuccess}</p>}
          </div>
        )}
        {activeTab === "vote" && (
          <div className="vote-proposal action-box">
            <h3>Vote on Proposals</h3>
            <div className="form-group">
              <label>Select Proposal</label>
              <select
                className="input"
                onChange={(e) =>
                  setSelectedProposal(
                    proposals.find((p) => p.id.toString() === e.target.value)
                  )
                }
                disabled={isLoading}
              >
                <option value="">Select Proposal</option>
                {proposals
                  .filter(
                    (p) =>
                      !p.executed &&
                      !p.vetoed &&
                      p.endTime > Math.floor(Date.now() / 1000)
                  )
                  .map((proposal) => (
                    <option key={proposal.id} value={proposal.id}>
                      Proposal #{proposal.id}: {proposal.description}
                    </option>
                  ))}
              </select>
            </div>
            {selectedProposal && (
              <div className="proposal-details">
                <p>
                  <strong>Description:</strong> {selectedProposal.description}
                </p>
                <p>
                  <strong>Type:</strong> {proposalTypes[selectedProposal.proposalType]}
                </p>
                <p>
                  <strong>New Value:</strong>{" "}
                  {getValueDisplay(selectedProposal.proposalType, selectedProposal.newValue)}
                </p>
                <p>
                  <strong>Target Address:</strong> {selectedProposal.targetAddress}
                </p>
                <p>
                  <strong>Voting Ends:</strong>{" "}
                  {new Date(selectedProposal.endTime * 1000).toLocaleString()}
                </p>
                <div className="form-group">
                  <label>Vote Weight (CRW)</label>
                  <input
                    type="text"
                    value={voteWeight}
                    onChange={(e) => setVoteWeight(e.target.value)}
                    placeholder="Enter vote weight (e.g. 10)"
                    className="input"
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Vote</label>
                  <select
                    value={inFavor}
                    onChange={(e) => setInFavor(e.target.value === "true")}
                    className="input"
                    disabled={isLoading}
                  >
                    <option value="true">For</option>
                    <option value="false">Against</option>
                  </select>
                </div>
                <button
                  onClick={handleVote}
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Voting..." : "Submit Vote"}
                </button>
              </div>
            )}
            {voteError && <p className="error">{voteError}</p>}
            {voteSuccess && <p className="success">{voteSuccess}</p>}
          </div>
        )}
        {activeTab === "view" && (
          <div className="proposal-list">
            <h3>All Proposals</h3>
            {listError && <p className="error">{listError}</p>}
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Status</th>
                  <th>Voting End</th>
                  {account?.toLowerCase() === OWNER_ADDRESS.toLowerCase() && (
                    <th>Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {proposals.map((proposal) => (
                  <tr key={proposal.id}>
                    <td>{proposal.id}</td>
                    <td>{proposal.description}</td>
                    <td>{proposalTypes[proposal.proposalType]}</td>
                    <td>{getValueDisplay(proposal.proposalType, proposal.newValue)}</td>
                    <td>{getStatus(proposal)}</td>
                    <td>{new Date(proposal.endTime * 1000).toLocaleString()}</td>
                    {account?.toLowerCase() === OWNER_ADDRESS.toLowerCase() && (
                      <td>
                        {getStatus(proposal) === "Proposal is under review by the blockchain council" && (
                          <>
                            <button
                              onClick={() => handleReview(proposal.id, "approved")}
                              className="btn btn-success"
                              disabled={isLoading}
                            >
                              Approved
                            </button>
                            <button
                              onClick={() => handleReview(proposal.id, "rejected")}
                              className="btn btn-danger"
                              disabled={isLoading}
                              style={{ marginLeft: "10px" }}
                            >
                              Rejected
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {activeTab === "results" && (
          <div className="voting-results">
            <h3>Voting Results</h3>
            {listError && <p className="error">{listError}</p>}
            {proposals.filter(
              (p) =>
                !p.executed &&
                !p.vetoed &&
                p.endTime > Math.floor(Date.now() / 1000)
            ).length === 0 && (
              <p className="info">No active proposals to display.</p>
            )}
            <div className="results-container">
              {proposals
                .filter(
                  (p) =>
                    !p.executed &&
                    !p.vetoed &&
                    p.endTime > Math.floor(Date.now() / 1000)
                )
                .map((proposal) => (
                  <div key={proposal.id} className="proposal-result-card">
                    <h4>Proposal #{proposal.id}: {proposal.description}</h4>
                    <p>
                      <strong>Type:</strong> {proposalTypes[proposal.proposalType]}
                    </p>
                    <p>
                      <strong>New Value:</strong>{" "}
                      {getValueDisplay(proposal.proposalType, proposal.newValue)}
                    </p>
                    <div className="vote-stats">
                      <p>
                        <strong>Votes For:</strong> {parseFloat(proposal.totalVotesFor).toFixed(2)} CRW
                      </p>
                      <p>
                        <strong>Votes Against:</strong> {parseFloat(proposal.totalVotesAgainst).toFixed(2)} CRW
                      </p>
                    </div>
                    <div className="countdown">
                      <p>
                        <strong>Time until voting ends:</strong>{" "}
                        {timeRemaining[proposal.id]
                          ? `${timeRemaining[proposal.id].days}d ${timeRemaining[proposal.id].hours}h ${timeRemaining[proposal.id].minutes}m ${timeRemaining[proposal.id].seconds}s`
                          : "Voting has ended"}
                      </p>
                    </div>
                    <div className="pie-chart">
                      <Pie
                        data={getPieChartData(proposal)}
                        options={{
                          responsive: true,
                          plugins: {
                            legend: {
                              position: "top",
                              labels: {
                                color: "#333",
                                font: {
                                  size: 14
                                }
                              }
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  if (context.label === "No votes") {
                                    return "No votes";
                                  }
                                  const value = context.raw;
                                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                  const percentage = ((value / total) * 100).toFixed(2);
                                  return `${context.label}: ${value.toFixed(2)} CRW (${percentage}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrownGovernanceDao;